import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import {
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
} from "@/layout/AppComponents";
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { axiosPath } from '../../../../routing/helpers/router';
import Translator from '../../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import AppSubmitButton from '../../../../layout/components/form/AppSubmitButton';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../store/actions/currentPageAction';
import ModuleComsaEnvironmentalSideBarReports from '../ModuleComsaEnvironmentalSideBarReports';
import ModuleComsaEnvironmentalConstructionUseNormalAspectsCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalConstruction/ModuleComsaEnvironmentalConstructionUseNormalAspectsCard';
import ModuleComsaEnvironmentalTitleCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalTitleCard';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';

export default function ModuleComsaEnvironmentalConstructionUseNormalAspectsScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [initialValues, setInitialValues] = useState();
  const [filterApplicable, setFilterApplicable] = useState(false);

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "constructionUseNormalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(r.data?.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "constructionUseNormalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="construction"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_construction_use_normal_aspects" />
        <div className="row">
          <div className="col-12">
            <Title className="mb-4">
              <Translator
                file="module-comsaEnvironmental"
                name="use.normal.aspects"
              />
            </Title>
          </div>
        </div>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <FilterApplicable
                  className="mt-2 mb-2"
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="filter.applicable"
                    />
                  )}
                  onClick={() => setFilterApplicable(!filterApplicable)}
                  $filtered={filterApplicable}
                >
                  <AppRemixIcon icon="filter" className="mr-2" size={16} />
                  {filterApplicable ? (
                    <Translator
                      file="module-comsaEnvironmental"
                      name="remove.applicable.filter"
                    />
                  ) : (
                    <Translator
                      file="module-comsaEnvironmental"
                      name="filter.applicable"
                    />
                  )}
                </FilterApplicable>
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="emissions.particle.emissions"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="air.pollution"
                      />
                      <Translator
                        file="module-comsaEnvironmental"
                        name="air.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="demolitions"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[0]}
                  setFieldValue={(n, v) => setFieldValue(`0.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="cleaning"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[1]}
                  setFieldValue={(n, v) => setFieldValue(`1.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="emissions.combustion.gas.emission"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="air.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.circulation.infrastructure.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[2]}
                  setFieldValue={(n, v) => setFieldValue(`2.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.vehicles.machinery"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[3]}
                  setFieldValue={(n, v) => setFieldValue(`3.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.vibrations.noise"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[4]}
                  setFieldValue={(n, v) => setFieldValue(`4.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="demolitions"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[5]}
                  setFieldValue={(n, v) => setFieldValue(`5.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.vehicles.machinery"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[6]}
                  setFieldValue={(n, v) => setFieldValue(`6.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.vibrations.vibrations"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="vibration.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[7]}
                  setFieldValue={(n, v) => setFieldValue(`7.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="aggregates"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[8]}
                  setFieldValue={(n, v) => setFieldValue(`8.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="discharges.water.discharges"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="water.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[9]}
                  setFieldValue={(n, v) => setFieldValue(`9.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="sanitary.waters"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[10]}
                  setFieldValue={(n, v) => setFieldValue(`10.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="machinery"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[11]}
                  setFieldValue={(n, v) => setFieldValue(`11.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="cleaning"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[12]}
                  setFieldValue={(n, v) => setFieldValue(`12.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="watercourses.affections.watercourses"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="water.pollution.resource.depletion"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[13]}
                  setFieldValue={(n, v) => setFieldValue(`13.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="temporary.intakes"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[14]}
                  setFieldValue={(n, v) => setFieldValue(`14.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="marine.coastal.environment.bar.alteration.water.seabed.characteristics"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="affects.water.pollution.ecosystem.alteration.species.disappearance"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[15]}
                  setFieldValue={(n, v) => setFieldValue(`15.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="conservation.tasks.machinery.circulation.sea"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[16]}
                  setFieldValue={(n, v) => setFieldValue(`16.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="natural.resources.consumption.raw.materials.water.fuel.electricity"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="depletion.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="spare.parts"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[17]}
                  setFieldValue={(n, v) => setFieldValue(`17.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="natural.resources.bar.water.consumption"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="depletion.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="cleaning.watering"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[18]}
                  setFieldValue={(n, v) => setFieldValue(`18.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="natural.resources.bar.fuel.consumption"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="depletion.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[19]}
                  setFieldValue={(n, v) => setFieldValue(`19.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="vehicles.machinery"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[20]}
                  setFieldValue={(n, v) => setFieldValue(`20.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="natural.resources.bar.electricity.consumption"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="depletion.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="infrastructure.lighting"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[21]}
                  setFieldValue={(n, v) => setFieldValue(`21.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="electrical.appliances"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[22]}
                  setFieldValue={(n, v) => setFieldValue(`22.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.urban.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.pollution.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="discharges.from.users.infrastructure"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[23]}
                  setFieldValue={(n, v) => setFieldValue(`23.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="assimilable.urban.solid.waste"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[24]}
                  setFieldValue={(n, v) => setFieldValue(`24.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.inert.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.pollution.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="discharges.from.users.infrastructure"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[25]}
                  setFieldValue={(n, v) => setFieldValue(`25.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="pruning.remains"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[26]}
                  setFieldValue={(n, v) => setFieldValue(`26.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.non.hazardous.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.pollution.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="discharges.from.users.infrastructure"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[27]}
                  setFieldValue={(n, v) => setFieldValue(`27.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="packaging.packaging.materials"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[28]}
                  setFieldValue={(n, v) => setFieldValue(`28.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.hazardous.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="operation"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="discharges.from.users.infrastructure"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[29]}
                  setFieldValue={(n, v) => setFieldValue(`29.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="oils.containers.machinery.filters"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[30]}
                  setFieldValue={(n, v) => setFieldValue(`30.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="signaling.marking.batteries"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[31]}
                  setFieldValue={(n, v) => setFieldValue(`31.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="signaling.paint.container"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[32]}
                  setFieldValue={(n, v) => setFieldValue(`32.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="fuel.container"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[33]}
                  setFieldValue={(n, v) => setFieldValue(`33.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="socioeconomic.impact.bar.economic.activity"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="increase.decrease.economic.activity"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="increase.decrease.economic.activity"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[34]}
                  setFieldValue={(n, v) => setFieldValue(`34.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="socioeconomic.impact.bar.economic.activity"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="job.creation"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="job.creation"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation.maintenance.conservation"
                    />
                  }
                  values={values?.[35]}
                  setFieldValue={(n, v) => setFieldValue(`35.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="socioeconomic.impact.bar.effects.on.mobility"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="effects.mobility"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.or.pedestrian.detours.diversions"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.[36]}
                  setFieldValue={(n, v) => setFieldValue(`36.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.or.pedestrian.detours.diversions"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.[37]}
                  setFieldValue={(n, v) => setFieldValue(`37.${n}`, v)}
                />
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
                <Footer className="mt-5 mb-5">
                  <Translator
                    file="module-comsaEnvironmental"
                    name="best.practices.included.maintenance.manual.built.project"
                  />
                </Footer>
              </form>
            )}
          </Formik>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const Footer = styled.h3`
  text-align: center;
  font-size: 18px;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;

const FilterApplicable = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background-color: #28a745;
  color: #fff;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid #28a745;
  color: ${(props) => (props.$filtered ? '#28a745' : '#fff')};
  background-color: ${(props) => (props.$filtered ? '#fff' : '#28a745')};
`;
